import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Mail, Lock, User as UserIcon, Phone, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CustomerLogin() {
  const location = useLocation();
  // Patch A.1 — support /register route + query params: ?email=&next=&mode=
  const searchParams = new URLSearchParams(location.search);
  const initialMode = (location.pathname === "/register" || searchParams.get("mode") === "register") ? "register" : "login";
  const [mode, setMode] = useState(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: searchParams.get("email") || "",
    password: "",
    phone: "",
  });
  const { login } = useCustomerAuth();
  const navigate = useNavigate();
  // CHANGED: Also read redirect from URL search params (for Build Your Look flow)
  const from = searchParams.get("next") || searchParams.get("redirect") || location.state?.from || "/account";

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !form.phone.trim()) {
      toast.error("Phone number is required");
      return;
    }
    if (mode === "forgot") {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/customer/auth/forgot-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: form.email }),
        });
        const data = await res.json().catch(() => ({}));
        toast.success(data.message || "If an account exists for this email, a reset link has been sent.");
        setMode("login");
      } catch {
        toast.error("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    try {
      const endpoint = mode === "register" ? "/api/customer/auth/register" : "/api/customer/auth/login";
      const body = mode === "register" ? form : { email: form.email, password: form.password };
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      // Read response as text first to avoid body stream issues
      let responseText = "";
      let data = {};
      try {
        responseText = await res.text();
        if (responseText) {
          data = JSON.parse(responseText);
        }
      } catch (parseError) {
        // JSON parsing failed, use text as detail
        data = { detail: responseText || "Invalid email or password" };
      }
      if (!res.ok) {
        throw new Error(data.detail || "Invalid email or password");
      }
      login({ customer_id: data.customer_id, email: data.email, name: data.name }, data.session_token);
      toast.success(mode === "register" ? "Account created!" : "Welcome back!");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F0] flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-[400px]">
          <Link to="/" className="flex items-center gap-1.5 text-[#676664]/50 text-[11px] tracking-[0.1em] uppercase mb-10 hover:text-[#676664]">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Mahra
          </Link>

          <h1 className="text-2xl text-[#282828] font-light mb-1" data-testid="auth-title">
            {mode === "login" ? "Welcome Back" : "Create Account"}
          </h1>
          <p className="text-[12px] text-[#676664]/60 font-light mb-8">
            {mode === "login" ? "Sign in to manage your bookings" : "Join Mahra to track your rentals"}
          </p>

          {/* Google Login */}
          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 py-3 border border-[#282828]/10 text-[12px] text-[#282828] hover:bg-white transition-colors mb-6"
            data-testid="google-login-btn"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-[#282828]/10" />
            <span className="text-[10px] text-[#676664]/40 uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-[#282828]/10" />
          </div>

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === "register" && (
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/30" />
                <Input
                  placeholder="Full name"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  className="pl-10 py-5 border-[#282828]/10 text-[13px]"
                  required
                  data-testid="register-name"
                />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/30" />
              <Input
                type="email"
                placeholder="Email address"
                value={form.email}
                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                className="pl-10 py-5 border-[#282828]/10 text-[13px]"
                required
                data-testid="auth-email"
              />
            </div>
            {mode !== "forgot" && (
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/30" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={form.password}
                  onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                  className="pl-10 pr-10 py-5 border-[#282828]/10 text-[13px]"
                  required
                  minLength={6}
                  data-testid="auth-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#676664]/30">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            )}
            {mode === "register" && (
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#676664]/30" />
                <Input
                  placeholder="Phone number *"
                  value={form.phone}
                  onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                  className="pl-10 py-5 border-[#282828]/10 text-[13px]"
                  required
                  type="tel"
                  data-testid="register-phone"
                />
              </div>
            )}
            {mode === "login" && (
              <div className="text-right -mt-2">
                <button
                  type="button"
                  onClick={() => setMode("forgot")}
                  className="text-[11px] text-[#9A7B4F] hover:underline tracking-wider"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#282828] hover:bg-[#1a1a1a] text-white py-5 text-[11px] tracking-[0.15em] uppercase"
              data-testid="auth-submit-btn"
            >
              {loading ? "Please wait..." : mode === "login" ? "Sign In" : mode === "forgot" ? "Send Reset Link" : "Create Account"}
            </Button>
          </form>

          <p className="text-center text-[12px] text-[#676664]/60 mt-6">
            {mode === "login" ? (
              <>Don't have an account? <button onClick={() => setMode("register")} className="text-[#9A7B4F] hover:underline" data-testid="switch-to-register">Create one</button></>
            ) : mode === "forgot" ? (
              <>Remembered your password? <button onClick={() => setMode("login")} className="text-[#9A7B4F] hover:underline" data-testid="back-to-login">Back to sign in</button></>
            ) : (
              <>Already have an account? <button onClick={() => setMode("login")} className="text-[#9A7B4F] hover:underline" data-testid="switch-to-login">Sign in</button></>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
